import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import {
  AdapterOptions,
  FlatAnswerMutation,
  SectionAdapter
} from 'components/Assessments/adapters/types'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'

/**
 * Physician Notification subsection adapter
 * Handles physician notification thresholds with nested less/greater pairs (14 fields)
 * This handles vital sign thresholds that trigger physician notifications
 */
export class PhysicianNotificationAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Temperature thresholds
    const tempLess = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_LESS
    )?.at(0)

    const tempGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_GREATER
    )?.at(0)

    // Pulse thresholds
    const pulseLess = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_LESS
    )?.at(0)

    const pulseGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_GREATER
    )?.at(0)

    // Respirations thresholds
    const respirationsLess = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS
    )?.at(0)

    const respirationsGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER
    )?.at(0)

    // Blood pressure thresholds
    const bpLess = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_LESS
    )?.at(0)

    const bpGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_GREATER
    )?.at(0)

    // Weight thresholds
    const weightLess = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_LESS
    )?.at(0)

    const weightGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_GREATER
    )?.at(0)

    // EtCO2 saturation thresholds
    const etco2SaturationLess = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS
    )?.at(0)

    const etco2SaturationGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER
    )?.at(0)

    // O2 saturation thresholds
    const o2SaturationLess = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS
    )?.at(0)

    const o2SaturationGreater = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER
    )?.at(0)

    return {
      [RecertFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS]: tempLess,
      [RecertFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER]: tempGreater,
      [RecertFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS]: pulseLess,
      [RecertFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER]: pulseGreater,
      [RecertFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS]: respirationsLess,
      [RecertFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER]: respirationsGreater,
      [RecertFormField.PHYSICIAN_NOTIFICATION_BP_LESS]: bpLess,
      [RecertFormField.PHYSICIAN_NOTIFICATION_BP_GREATER]: bpGreater,
      [RecertFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS]: weightLess,
      [RecertFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER]: weightGreater,
      [RecertFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS]: etco2SaturationLess,
      [RecertFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER]: etco2SaturationGreater,
      [RecertFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS]: o2SaturationLess,
      [RecertFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER]: o2SaturationGreater
    }
  }

  buildFlatAnswers(formData: RecertFormData): FlatAnswerMutation[] {
    return [
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_LESS,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_TEMP_LESS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_TEMP_GREATER,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_TEMP_GREATER]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_LESS,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_PULSE_LESS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_PULSE_GREATER,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_PULSE_GREATER]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_LESS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_RESPIRATIONS_GREATER]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_LESS,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_BP_LESS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_BP_GREATER,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_BP_GREATER]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_LESS,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_WEIGHT_LESS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_WEIGHT_GREATER,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_WEIGHT_GREATER]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_LESS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_ETCO2_SATURATION_GREATER]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_LESS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER,
        value: formData[RecertFormField.PHYSICIAN_NOTIFICATION_O2_SATURATION_GREATER]
      }
    ]
  }
}
